<template>
  <BaseLayout>
    <BaseDashboard :is-loading="isLoading">
      <div class="h-100 overflow-auto d-flex flex-column">
        <div
          class="page-header bg-secondary m-0 p-3 border-bottom"
        >
          <h1 class="page-title">
            {{ reportingTitlePrefix }} - {{ campaignName }}
          </h1>
          <div class="page-options d-flex">
            <b-btn
              v-if="mode==='SHARER'"
              variant="secondary-outline"
              class="btn btn-secondary btn-sm mr-2"
              data-test="showcase-share-button"
              v-b-modal.modalSharing
              v-b-tooltip.hover
              title="Share this report with third parties"
            >
              Share
              <i class="fal fa-share-square ml-2" />
            </b-btn>
          </div>
        </div>
        <b-tabs
          class="overflow-x-hidden campaign-edit-nav d-flex flex-column h-100"
          nav-wrapper-class="bg-white border-bottom panel-nav"
          nav-class="px-3"
          content-class="h-100"
          v-model="tabIndex"
          lazy
        >
          <b-tab
            title-link-class="campaign-overview-tab"
            active
          >
            <template v-slot:title>
              Campaign Overview
            </template>
            <campaign-overview />
          </b-tab>
          <b-tab
            title-link-class="publisher-performance-tab"
          >
            <template v-slot:title>
              Publisher Performance
            </template>
            <publisher-performance />
          </b-tab>
          <b-tab
            title-link-class="campaign-performance"
          >
            <template v-slot:title>
              Creative Performance
            </template>
            <creative-performance />
          </b-tab>
          <b-tab
            title-link-class="content-report-tab"
            class="h-100"
          >
            <template v-slot:title>
              Content Performance
            </template>
            <content-performance />
          </b-tab>
          <b-tab
            title-link-class="campaign-summary-tab"
            class="h-100"
            v-if="reportingStatementsArePresent && GET_IS_MANAGED_CAMPAIGN"
          >
            <template v-slot:title>
              Campaign Summary
            </template>
            <campaign-summary />
          </b-tab>
          <b-tab
            title-link-class="screenshots-tab"
            class="h-100"
            v-if="isCampaignScreenshotsTabDisplayed"
          >
            <template v-slot:title>
              Campaign Screenshots
            </template>
            <campaign-screenshots />
          </b-tab>
          <template
            v-if="isPublishButtonDisplayed"
            #tabs-end
          >
            <button
              role="presentation"
              class="btn btn-secondary btn-sm ml-auto h-50 align-self-center mr-4"
              @click="openModal()"
            >
              Publish
            </button>
          </template>
        </b-tabs>
      </div>
    </BaseDashboard>
    <ShareModal
      :campaign="campaign"
      :share-type="'REPORTING'"
      v-if="mode==='SHARER'"
    />
    <b-modal
      id="ReportStatementsPublishConfirm"
      title="Ready to publish?"
      hide-header-close
      no-close-on-esc
      no-close-on-backdrop
    >
      <p class="mb-0">
        Pressing 'Confirm' will make the End of Campaigns highlights available to everyone with a link to the Reporting Dashboard across all tabs - make sure you have reviewed your changes!
      </p>
      <template #modal-footer>
        <b-button
          class="btn-sm"
          variant="tertiary"
          @click="closeModal()"
        >
          Cancel
        </b-button>
        <b-button
          class="btn-sm"
          variant="primary"
          @click="publishCampaignReportStatements()"
          :class="{ 'btn-loading': isPublishingReportStatements }"
        >
          Confirm
        </b-button>
      </template>
    </b-modal>
  </BaseLayout>
</template>

<script>
import {
  mapActions, mapGetters, mapState,
} from 'vuex';
import helper from '@/core/helper';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as accountTypes from '@/modules/account/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';

import CampaignOverview from '@/modules/reporting/components/CampaignOverview.vue';
import CreativePerformance from '@/modules/reporting/components/CreativePerformance/CreativePerformance.vue';
import ContentPerformance from '@/modules/reporting/components/ContentPerformance.vue';
import CampaignSummary from '@/modules/reporting/components/CampaignSummary.vue';
import PublisherPerformance from '@/modules/reporting/components/PublisherPerformance.vue';

import ShareModal from '@/modules/share/components/ShareModal';
import CampaignScreenshots from '@/modules/reporting/components/CampaignScreenshots.vue';

export default {
  name: 'ReportingIndex',
  components: {
    CampaignScreenshots,
    CampaignOverview,
    CreativePerformance,
    ContentPerformance,
    CampaignSummary,
    PublisherPerformance,
    ShareModal,
  },
  data() {
    return {
      campaign: {},
      isLoading: false,
      tabIndex: 0,
      campaignName: '',
      campaignId: 0,
      mode: '',
      isPublishingReportStatements: false,
      campaignScreenshotNames: [],
    };
  },
  computed: {
    ...mapState('reporting', [
      'campaignReportConfiguration',
      'reportingStatements',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_STATE,
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
    ]),
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    isCampaignScreenshotsTabDisplayed() {
      return this.campaignScreenshotNames.length > 0 || this.GET_IS_MANAGED_ACCOUNT;
    },
    isPublishButtonDisplayed() {
      return this.GET_IS_MANAGED_ACCOUNT
      && this.GET_IS_CAMPAIGN_STATE('ENDED')
      && this.campaignReportConfiguration.published === false
      && helper.objLength(this.reportingStatements) > 0;
    },
    reportingTitlePrefix() {
      return this.GET_IS_CAMPAIGN_STATE('ENDED') ? 'End of Campaign Reporting' : 'Reporting';
    },
    reportingStatementsArePresent() {
      return this.reportingStatements.CAMPAIGN_OVERVIEW?.length > 0;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.RESET_CAMPAIGN,
      campaignTypes.GET_CAMPAIGN_BY_ID,
      campaignTypes.FETCH_ORDER,
      campaignTypes.FETCH_CURRENCIES,
      campaignTypes.FETCH_ADVERTISER,
    ]),
    ...mapActions('reporting', [
      reportingTypes.PUBLISH_CAMPAIGN_REPORT_STATEMENTS,
      reportingTypes.FETCH_CAMPAIGN_SCREENSHOT_NAMES,
    ]),
    openModal() {
      this.$root.$emit('bv::show::modal', 'ReportStatementsPublishConfirm');
    },
    closeModal() {
      this.$root.$emit('bv::hide::modal', 'ReportStatementsPublishConfirm');
    },
    async publishCampaignReportStatements() {
      this.isPublishingReportStatements = true;
      try {
        await this.PUBLISH_CAMPAIGN_REPORT_STATEMENTS();
      } finally {
        this.closeModal();
        this.isPublishingReportStatements = false;
      }
    },
    async handleCreate() {
      this.isLoading = true;
      const { campaignId, advertiserId } = this.$route.params;
      this.campaignId = parseInt(campaignId, 10);
      this.RESET_CAMPAIGN();
      try {
        await this.FETCH_ADVERTISER(advertiserId);
        const campaignPromise = this.GET_CAMPAIGN_BY_ID(campaignId);
        const orderPromise = this.FETCH_ORDER({ advertiserId, campaignId, setOrder: true });
        const currenciesPromise = this.FETCH_CURRENCIES();
        Promise.all([campaignPromise, orderPromise, currenciesPromise])
          .then((result) => {
            this.mode = result[0].shareMode;
            this.campaign = {
              id: result[1].campaignId,
              ...result[0],
            };
            this.campaignName = result[0].name;
            this.FETCH_CAMPAIGN_SCREENSHOT_NAMES().then((screenshotNames) => {
              this.campaignScreenshotNames = screenshotNames;
            });
            this.isLoading = false;
          })
          .catch(() => {
            this.$router.back();
          });
      } catch (err) {
        this.$router.back();
      }
    },
  },
  async created() {
    this.handleCreate();
  },
};
</script>
